import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import AppDocTabs from "../components/AppDocTabs";
import AppIcon from "../components/AppIcon";
import { APPS, getApp } from "../data/apps";
import { SITE } from "../data/site";
import { AppLinks, AppStatus } from "../types/app";
import NotFoundPage from "./NotFoundPage";
import "../styles/detail.css";

const GET_LABEL: Record<keyof AppLinks, string> = {
  app_store: "App Store",
  google_play: "Google Play",
  web: "웹에서 열기",
};
const STATUS_LABEL: Record<AppStatus, string> = { live: "운영 중", beta: "베타", review: "스토어 심사 중" };
const num = (i: number) => String(i + 1).padStart(2, "0");
const host = (url?: string) => {
  try {
    return url ? new URL(url).host : "";
  } catch {
    return "";
  }
};

export default function AppDetailPage() {
  const { slug = "" } = useParams();
  const app = getApp(slug);

  useEffect(() => {
    if (app) document.title = `${app.name} | kwangbok.me`;
  }, [app]);

  if (!app) return <NotFoundPage />;

  // D-18: 스토어와 외부 링크는 새 창으로 연다. links에 있는 것만 버튼으로.
  const gets = (Object.keys(GET_LABEL) as (keyof AppLinks)[]).filter((k) => app.links?.[k]);

  return (
    <SiteLayout crumb={`apps / ${app.slug}`}>
      <div className="detail">
        <header className="detail-head">
          <div className="detail-id">
            <AppIcon app={app} className="detail-icon" />
            <div className="detail-id-text">
              <h1>{app.name}</h1>
              <p className="muted">{app.summary}</p>
              <div className="detail-tags">
                {app.platforms.map((p) => (
                  <span key={p} className="tag">{p}</span>
                ))}
                <span className={`tag tag--${app.status}`} title={STATUS_LABEL[app.status]}>
                  {app.status}
                  <span className="sr-only">, {STATUS_LABEL[app.status]}</span>
                </span>
              </div>
            </div>
          </div>
          {gets.length === 0 && app.status === "review" && (
            <div className="detail-gets">
              <span className="detail-get detail-get--pending">스토어 심사 중</span>
            </div>
          )}
          {gets.length > 0 && (
            <div className="detail-gets">
              {gets.map((k) => (
                <a key={k} href={app.links![k]} target="_blank" rel="noopener noreferrer" className="detail-get">
                  {GET_LABEL[k]}
                  <span className="sr-only"> (새 창)</span>
                </a>
              ))}
            </div>
          )}
        </header>

        <AppDocTabs app={app} current="about" />

        <section className="card detail-pad detail-why-card" aria-labelledby="detail-why-title">
          <h2 id="detail-why-title" className="eyebrow">why</h2>
          <p className="muted detail-why">{app.why}</p>
        </section>

        {/* 스크린샷은 한 프레임 안에 나란히. 폭이 좁으면 가로로 넘겨 본다 */}
        {app.screenshots && app.screenshots.length > 0 && (
          <figure className="card detail-shot">
            <div className="detail-shot-bar" aria-hidden="true">
              <span />
              <span />
              <span className="mono muted">{host(app.links?.web) || app.slug}</span>
            </div>
            <div className="detail-shot-body">
              {app.screenshots.map((s) => (
                <a key={s.src} href={s.src} target="_blank" rel="noopener noreferrer" className="detail-shot-link">
                  <img src={s.src} alt={s.alt} loading="lazy" />
                  <span className="sr-only"> (원본 이미지, 새 창)</span>
                </a>
              ))}
            </div>
          </figure>
        )}

        {app.features.length > 0 && (
          <section className="detail-features" aria-label="주요 기능">
            {app.features.map((f, i) => (
              <div key={f.title} className="card detail-pad detail-feature">
                <span className="eyebrow">feature {num(i)}</span>
                <h2>{f.title}</h2>
                <p className="muted">{f.body}</p>
              </div>
            ))}
          </section>
        )}

        <div className="detail-bottom">
          <Link to="/#career" className="detail-maker">
            <span className="mono detail-maker-badge">core</span>
            <span className="detail-maker-text">
              <span className="mono">만든 사람</span>
              <strong>{SITE.owner}</strong>
              <span>{app.role}</span>
              <span>AI 네이티브 프로덕트 엔지니어. 경력 보기</span>
            </span>
          </Link>
          <a href={SITE.portfolio_pdf} download className="card detail-pad detail-doc">
            <span className="eyebrow">portfolio.pdf</span>
            <strong>설계 고민 문서 받기</strong>
            <p className="muted">서비스마다 서버 비용을 어떻게 줄이고 손익분기를 어떻게 맞췄는지 정리한 PDF입니다.</p>
          </a>
        </div>

        <nav className="detail-others" aria-label="다른 앱">
          <span className="mono muted">다른 앱</span>
          {APPS.filter((a) => a.slug !== app.slug).map((a) => (
            <Link key={a.slug} to={`/apps/${a.slug}`} className="chip">
              {a.name}
            </Link>
          ))}
          <Link to="/apps" className="chip">전체 목록</Link>
        </nav>
      </div>
    </SiteLayout>
  );
}
